import { useEffect, useState } from "react";
import { Button, ButtonGroup, Input } from "@heroui/react";
import Calendary from "@/components/calendary";
import { Icon } from "@iconify/react";
import Game from "@/pages/game";
import { isMobile } from "@/stores/MenuStore";
import LiveIcon from "./live-icon";
import { gameStates } from "@/utils/gameStates";
import { X } from 'lucide-react';
import Fuse from 'fuse.js';
import debounce from 'lodash/debounce';
import AccordionGames from "./accordion-games";
import { localTimeToUnixTimestamp, newDateWithAddedSeconds } from "@/utils/time";
import { fetchDataAfterRefresh, useUserStore } from "@/components/login/loginStore";
import { fetchWithArgs } from "@/utils/requests";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";

interface Game {
    aggregate_id: string | null;
    away_id: number;
    away_name: string;
    created_at: number;
    details: string | null;
    group_id: string | null;
    has_odds: boolean;
    has_premium_odds: boolean;
    home_id: number;
    home_name: string;
    id: number;
    league_id: number;
    league_name: string;
    league_country_id: number;
    leg: string;
    length: number;
    name: string;
    placeholder: boolean;
    result_info: string | null;
    round_id: number;
    season_id: number;
    sport_id: number;
    stage_id: number;
    starting_at: string;
    starting_at_timestamp: number;
    state_id: number;
    updated_at: number;
    venue_id: number;
};

export interface League {
    league_id: number;
    league_name: string;
    country_id: number;
    data: Game[];
};

export interface FavoriteLeague {
    created_at: string | null;
    id: number;
    target_id: number;
    target_type: string;
    updated_at: string | null;
    user_id: number;
};

export const popularIdsLeagues = [4958, 9054, 9683, 35299];

const TELEGRAM_CTA_DISMISSED_KEY = 'telegram_cta_dismissed';

export default function GameList() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [date, setDate] = useState<string | null>(null);
    const [showTelegramCTA, setShowTelegramCTA] = useState<boolean>(
        !localStorage.getItem(TELEGRAM_CTA_DISMISSED_KEY)
    );
    const [games, setGames] = useState<League[] | []>([]);
    const [filterGames, setFilterGames] = useState<League[] | []>(games);
    const [selectGameState, setSelectGameState] = useState<string[]>(['scheduled', 'live', 'finished']);
    const [gameSearch, setGameSearch] = useState<string>('');
    const [favoritesLeagues, setFavoritesLeagues] = useState<FavoriteLeague[] | []>([]);
    const [favoritesGames, setFavoritesGames] = useState<League[] | []>([]);
    const user = useUserStore((state) => state);

    const handleSelectState = (newState: string) => {
        setSelectGameState((prevState) => {
            if (prevState.includes(newState)) return prevState.filter((state) => state !== newState);
            else return [...prevState, newState];
        });
    };

    const groupGamesByLeague = (games: Game[]): League[] => {
        const leagueMap = new Map<number, League>();
        games.forEach((game) => {

            const leagueId = game.league_id;
            if (!leagueMap.has(leagueId)) {
                leagueMap.set(leagueId, { league_id: leagueId, league_name: game.league_name, country_id: game.league_country_id, data: [] });
            }
            leagueMap.get(leagueId)?.data.push(game);
        });
        leagueMap.forEach((league) => {
            league.data.sort((a, b) => a.starting_at_timestamp - b.starting_at_timestamp);
        });
        const leaguesArray = Array.from(leagueMap.values());
        return leaguesArray.sort((a, b) => {
            const aIndex = popularIdsLeagues.indexOf(a.league_id);
            const bIndex = popularIdsLeagues.indexOf(b.league_id);
            const aPosition = aIndex === -1 ? popularIdsLeagues.length : aIndex;
            const bPosition = bIndex === -1 ? popularIdsLeagues.length : bIndex;
            return aPosition - bPosition;
        });
    };

    const getGamesByDate = () => {
        // fetchDataWithArgsAfterRefresh([burl('/api/fixture_info_by_date'), '/not_premium_endpoints/fixture_info_by_date.json'], 
        // making the request to the endpoint /api/fixture_info_by_date available to all users
        fetchWithArgs('/api/fixture_info_by_date',
            {
                start_ts: date ? localTimeToUnixTimestamp(date) : null, end_ts: date ? localTimeToUnixTimestamp(newDateWithAddedSeconds(new Date(date), 3600 * 24)) : null
            }, (data) => {
                setGames(groupGamesByLeague(data));
            });
    };

    useEffect(() => {
        if (date) getGamesByDate();
    }, [date]);

    useEffect(() => {
        const debouncedFilter = debounce(() => {
            const gamesByLeagues = games.map((league) => {
                const fuse = new Fuse(league.data, {
                    keys: ['home_name', 'away_name', 'league_name'],
                    threshold: 0.3,
                });
                const filteredBySearch = gameSearch
                    ? fuse.search(gameSearch).map((result) => result.item)
                    : league.data;
                const filteredByState =
                    selectGameState.length > 0
                        ? filteredBySearch.filter((game) => {
                            const filterIds = gameStates
                                .filter(({ state }) => selectGameState.includes(state))
                                .flatMap(({ state_ids }) => state_ids);
                            return filterIds.includes(game.state_id);
                        })
                        : filteredBySearch;

                return {
                    ...league,
                    data: filteredByState,
                };
            });
            setFilterGames(gamesByLeagues);
        }, 300);
        debouncedFilter();
        return () => debouncedFilter.cancel();
    }, [games, selectGameState, gameSearch]);
    useEffect(() => {
        let gamesByLeagues = games;
        if (selectGameState.length > 0) {
            const filterIds = gameStates
                .filter(({ state }) => selectGameState.includes(state))
                .flatMap(({ state_ids }) => state_ids);
            gamesByLeagues = games.map((league) => ({
                ...league,
                data: league.data.filter((game) => filterIds.includes(game.state_id)),
            }));
        }
        setFilterGames(gamesByLeagues);
    }, [games, selectGameState]);


    const getFavoritesLeagues = () => {
        fetchDataAfterRefresh('/api/get_favorite_target?target_type=league', (data) => {
            data = data.map((fav: any) => ({ ...fav, target_id: parseInt(fav.target_id) }));
            setFavoritesLeagues(data);
        }, user.premium);
    };
    useEffect(() => getFavoritesLeagues(), [games]);

    useEffect(() => {
        const favoriteLeagueIds = favoritesLeagues?.map(favorite => favorite.target_id);
        const filteredGames = filterGames.filter(league =>
            favoriteLeagueIds?.includes(league.league_id)
        );
        setFavoritesGames(filteredGames);
    }, [filterGames, favoritesLeagues]);

    return (
        <div className={`w-full fadeIn ${isMobile() ? 'gap-3 flex flex-col' : 'grid grid-cols-[320px_1fr] w-full gap-6 justify-center'}`}>
            <div className="flex flex-col gap-2 ">
                <Calendary isCard={true} selectedDate={date} onDateChange={setDate} />
            </div>
            <div className="flex flex-col col gap-2">
                {showTelegramCTA && (!user.logged || !user.premium) && (
                    <div className="relative flex items-center gap-4 bg-content1 border border-primary-500/50 rounded-xl p-4 fadeIn overflow-hidden">
                        <div className="absolute inset-0 opacity-5 pointer-events-none"
                            style={{ background: 'radial-gradient(ellipse at top left, var(--heroui-primary-500) 0%, transparent 70%)' }} />
                        <div className="flex-none flex items-center justify-center w-10 h-10 rounded-full bg-primary-500/15">
                            <Icon icon="bxl:telegram" className="text-primary-500 text-2xl" />
                        </div>
                        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                            <span className="font-bold text-sm text-default-900">{t('games_list.games_list_7')}</span>
                            <span className="text-xs text-default-500 leading-relaxed">{t('games_list.games_list_8')}</span>
                        </div>
                        <Button
                            size="sm"
                            className="flex-none bg-primary-500 text-black/80 font-bold"
                            onClick={() => navigate('/telegram-bot')}
                        >
                            <Icon icon="bxl:telegram" className="text-base" />
                            {t('games_list.games_list_9')}
                        </Button>
                        <button
                            className="absolute top-2 right-2 text-default-400 hover:text-default-600 transition"
                            onClick={() => { setShowTelegramCTA(false); localStorage.setItem(TELEGRAM_CTA_DISMISSED_KEY, '1'); }}
                        >
                            <Icon icon="mdi:close" className="text-base" />
                        </button>
                    </div>
                )}
                <div className="flex flex-wrap items-center min-h-[44px] gap-2 overflow-x-auto px-2">
                    <ButtonGroup className="mx-auto">
                        {gameStates.map((state) => (
                            <Button
                                key={state.state}
                                onClick={() => handleSelectState(state.state)}
                                className={`flex items-center ${selectGameState.includes(state.state) ? 'bg-primary text-white' : 'bg-default-100 text-default-500'}`}>
                                {state.state == 'live' ?
                                    <>
                                        <LiveIcon live={selectGameState.includes(state.state) || selectGameState.length <= 0} />
                                        {t(state.title)}
                                    </>
                                    : t(state.title)}
                            </Button>
                        ))}
                    </ButtonGroup>
                    <Input
                        startContent={
                            <Icon className="text-default-500" style={{ 'fontSize': '24px' }} icon="heroicons-outline:search" />
                        }
                        name="search-games"
                        placeholder={t('games_list.games_list_2')}
                        type="text"
                        variant="bordered"
                        value={gameSearch}
                        onChange={(e) => setGameSearch(e.target.value)}
                        className='md:max-w-xs'
                        endContent={
                            <X
                                className="cursor-pointer text-default-500"
                                style={{ fontSize: '24px' }}
                                onClick={() => setGameSearch('')}
                            />
                        }
                    />
                </div>
                {filterGames.length > 0 && filterGames.some((league) => league.data.length > 0) ?
                    <>
                        {(favoritesGames.length > 0) && (
                            <AccordionGames
                                gamesByLeagues={favoritesGames}
                                favoritesLeagues={favoritesLeagues}
                                setFavoritesLeagues={setFavoritesLeagues}
                                favorites={true}
                            />
                        )}
                        <AccordionGames
                            gamesByLeagues={filterGames}
                            favoritesLeagues={favoritesLeagues}
                            setFavoritesLeagues={setFavoritesLeagues}
                        />
                    </>
                    :
                    <div className="flex flex-col items-center justify-center mx-2 p-6 bg-default-100 rounded-large h-[200px] fadeIn">
                        <Icon className="text-default-400 text-5xl mb-4" icon="mdi:calendar-remove" />
                        <p className="text-center text-default-500 text-lg">
                            {t('games_list.games_list_3')}
                        </p>
                    </div>
                }
            </div>
        </div>
    );
}